/**
 * extensions/dicom-rt/src/components/LongitudinalStudy/sections/ResponseSection.js
 *
 * PATCH: add `RECIP10` to the `titles`, `items`, and `notes` maps so the
 * table renders RECIP-appropriate columns.
 *
 * Other changes: skip endpoint summary line for RECIP (no target lesion concept
 * → time-to-progression / duration-of-response from target measurements aren't
 * meaningful; we still show best overall response).
 *
 * Everything else is preserved verbatim.
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { utils } from '@platform/core';
import {
  Body,
  Container,
  Grid,
  Button,
  IconButton,
  Icon,
  Select,
  Input,
  H6,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@platform/ui';
import module from '../../../modules/dicom-measurement/src';

const { round } = utils;
const {
  guideline,
  getTimepoints,
  getAIAssessments,
  getEndpointsFromAssessments,
  getResponseAlerts,
  getAllNewLesionAlert,
} = module;
const { getResponseTypes } = guideline;

export const ResponseSection = ({
  imageSets,
  ROIContours,
  criteria,
  assessments,
  resetAssessments,
  onAssessmentsChange,
  onExportReportClick,
}) => {
  const timepoints = getTimepoints(imageSets, ROIContours);
  const ai_assessments = getAIAssessments(
    timepoints,
    ROIContours,
    criteria,
    assessments
  );

  const isRecip = criteria?.guidelineId === 'RECIP10';
  const isImmune = criteria?.guidelineId === 'iRECIST';

  /* RECIP assessment shape differs from RECIST/iRECIST (no nested Lesions
   * arrays on newLesion responses, no targetResponses array with per-lesion
   * stats), so the legacy endpoint / alert helpers would crash. Skip them
   * for RECIP. Best overall response is computed inline below. */
  const {
    timeToProgression: ttp,
    timeToResponse: ttr,
    durationOfResponse: dor,
    bestOverallResponse: bor,
  } = isRecip
    ? {
        timeToProgression: null,
        timeToResponse: null,
        durationOfResponse: null,
        bestOverallResponse: getRecipBestOverall(ai_assessments),
      }
    : getEndpointsFromAssessments(criteria, ai_assessments);

  /** alerts */
  const responseAlerts = isRecip ? [] : getResponseAlerts(ai_assessments);
  const existingAlert =
    !isRecip &&
    criteria.overallResponseAlert === 'ENABLED' &&
    responseAlerts.find(alert => alert.isWarning);
  const existingNewLesionAlert =
    !isRecip &&
    criteria.newLesionAlert === 'ENABLED' &&
    getAllNewLesionAlert(ai_assessments);
  const [isAlertDisplayed, setAlertDisplayed] = useState(true);
  const [isPasswordInputDisplayed] = useState(false);

  return (
    <Body>
      <Container>
        <Table stickyHeader size="small">
          <TableHead>
            <HeadRow
              criteria={criteria}
              isAlertDisplayed={isAlertDisplayed}
              setAlertDisplayed={setAlertDisplayed}
            />
          </TableHead>
          <TableBody>
            {ai_assessments.map((timepoint, index) => {
              return (
                <Row
                  key={timepoint.SeriesInstanceUID + index}
                  index={index}
                  timepoint={timepoint}
                  criteria={criteria}
                  onAssessmentsChange={onAssessmentsChange}
                  existingAlert={existingAlert}
                  existingNewLesionAlert={existingNewLesionAlert}
                  isAlertDisplayed={isAlertDisplayed}
                  isPasswordInputDisplayed={isPasswordInputDisplayed}
                />
              );
            })}
          </TableBody>
        </Table>
      </Container>
      <Divider />
      {isAlertDisplayed && existingAlert?.isWarning && (
        <div style={{ display: 'flex' }}>
          <IconButton disabled color="warning">
            <Icon
              name="warning"
              fill="var(--ui-yellow)"
              widht="24px"
              height="24px"
            />
          </IconButton>
          <H6 style={{ color: 'var(--ui-yellow)' }}>
            Warning: {existingAlert.message}
          </H6>
        </div>
      )}
      {isAlertDisplayed && existingNewLesionAlert?.isWarning && (
        <div style={{ display: 'flex' }}>
          <IconButton disabled color="warning">
            <Icon
              name="warning"
              fill="var(--ui-yellow)"
              widht="24px"
              height="24px"
            />
          </IconButton>
          <H6 style={{ color: 'var(--ui-yellow)' }}>
            Warning: {existingNewLesionAlert.message}
          </H6>
        </div>
      )}
      <div style={{ margin: '5px 0' }}>
        <Grid container spacing={2}>
          {!isImmune &&
            !isRecip &&
            [
              `Time to response: ${ttr} days`,
              `Duration of response: ${dor} days`,
              `Time to progression: ${ttp ? ttp + ' days' : 'N/A'}`,
              `Best overall response: ${bor?.result}`,
            ].map((text, i) => (
              <Grid key={i} item xs={3}>
                <H6>{text}</H6>
              </Grid>
            ))}
          {isRecip && (
            <>
              <Grid item xs={4}>
                <H6>{`PD reference: ${criteria.pdReference || 'NADIR'}`}</H6>
              </Grid>
              <Grid item xs={4}>
                <H6>{`New lesion accounting: ${criteria.newLesionAccounting ||
                  'CUMULATIVE'}`}</H6>
              </Grid>
              <Grid item xs={4}>
                <H6>{`Best overall response: ${bor?.result || 'N/A'}`}</H6>
              </Grid>
            </>
          )}
        </Grid>
      </div>
      <div style={{ margin: '5px 0' }}>
        <Grid container spacing={2}>
          {[
            ['Reset Assessments', () => resetAssessments(), false],
            ['Export Reports', () => onExportReportClick(), false],
          ].map(([text, handleClick, disabled], i) => (
            <Grid key={i} item xs={6}>
              <Button
                disabled={disabled}
                onClick={handleClick}
                color="secondary"
                variant="contained"
                medium
                fullWidth
              >
                {text}
              </Button>
            </Grid>
          ))}
        </Grid>
      </div>
    </Body>
  );
};
ResponseSection.propTypes = {
  imageSets: PropTypes.array,
  ROIContours: PropTypes.array,
  criteria: PropTypes.object,
  assessments: PropTypes.array,
  resetAssessments: PropTypes.func,
  onAssessmentsChange: PropTypes.func,
  onExportReportClick: PropTypes.func,
};

const HeadRow = ({ criteria, isAlertDisplayed, setAlertDisplayed }) => {
  const titles = id => {
    const map = {
      iRECIST: [
        ['Date', '10%'],
        ['ST', '6%'],
        ['Targets', '12%'],
        ['Non-targets', '12%'],
        ['New Lesion(T)', '12%'],
        ['New Lesion(NT)', '12%'],
        ['Clinical Status', '12%'],
        ['Overall', '12%'],
        ['', '6%'],
      ],
      /* --------- NEW: RECIP 1.0 --------- */
      RECIP10: [
        ['Date', '10%'],
        ['ST', '6%'],
        ['PSMA-VOL Δ', '24%'],
        ['New Lesion', '24%'],
        ['Overall', '24%'],
        ['', '6%'],
      ],
      /* ---------------------------------- */
      default: [
        ['Date', '10%'],
        ['ST', '6%'],
        ['Targets', '12%'],
        ['Non-targets', '12%'],
        ['New Lesion', '12%'],
        ['Use of Steroids', '12%'],
        ['Clinical Status', '12%'],
        ['Overall', '12%'],
        ['', '6%'],
      ],
    };
    return map[id] || map['default'];
  };

  return (
    <TableRow>
      <TableCell width={'6%'} header={1}>
        <IconButton
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
            setAlertDisplayed(!isAlertDisplayed);
          }}
          color="primary"
        >
          <Icon
            name={isAlertDisplayed ? 'alert-triangle' : 'triangle'}
            width="24px"
            height="24px"
          />
        </IconButton>
      </TableCell>
      {titles(criteria.guidelineId).map(([text, width], i) => (
        <TableCell key={i} width={width} header={1}>
          <H6>{text}</H6>
        </TableCell>
      ))}
    </TableRow>
  );
};
HeadRow.propTypes = {
  isAlertDisplayed: PropTypes.bool,
  setAlertDisplayed: PropTypes.func,
};

const Row = ({
  index,
  timepoint,
  criteria,
  onAssessmentsChange,
  existingAlert,
  isAlertDisplayed,
  isPasswordInputDisplayed,
}) => {
  const isBaseline = index === 0;
  const [isOverlayDisplayed, setOverlayDisplayed] = useState(false);
  const [isResponseConfirming, setIsResponseConfirming] = useState(false);
  const [isResponseConfirmed, setIsResponseConfirmed] = useState(false);
  const [reason, setReason] = useState('');
  const [password, setPassword] = useState('');

  const warning =
    isAlertDisplayed && timepoint.SeriesDate === existingAlert?.date;
  const items = id => {
    const response = getResponseTypes(id);
    const map = {
      iRECIST: [
        ['targetResponse', response.TARGET_RESPONSE],
        ['nonTargetResponse', response.NON_TARGET_RESPONSE],
        ['newLesionTargetResponse', response.NEW_LESION_TARGET_RESPONSE],
        ['newLesionNonTargetResponse', response.NEW_LESION_NON_TARGET_RESPONSE],
        ['clinicalStatus', response.CLINICAL_STATUS],
        ['overallResponse', response.OVERALL_RESPONSE],
      ],
      /* --------- NEW: RECIP 1.0 --------- */
      RECIP10: [
        ['targetResponse', response.TARGET_RESPONSE],
        ['newLesionResponse', response.NEW_LESION_RESPONSE],
        ['overallResponse', response.OVERALL_RESPONSE],
      ],
      /* ---------------------------------- */
      default: [
        ['targetResponse', response.TARGET_RESPONSE],
        ['nonTargetResponse', response.NON_TARGET_RESPONSE],
        ['newLesionResponse', response.NEW_LESION_RESPONSE],
        ['steroidsUse', response.USE_OF_STEROIDS],
        ['clinicalStatus', response.CLINICAL_STATUS],
        ['overallResponse', response.OVERALL_RESPONSE],
      ],
    };
    return map[id] || map['default'];
  };
  const notes = id => {
    const map = {
      iRECIST: [
        'targetResponse',
        'nonTargetResponse',
        'newLesionTargetResponse',
        'newLesionNonTargetResponse',
      ],
      /* --------- NEW: RECIP 1.0 --------- */
      RECIP10: ['targetResponse', 'newLesionResponse', 'overallResponse'],
      /* ---------------------------------- */
      default: ['targetResponse', 'nonTargetResponse', 'newLesionResponse'],
    };
    return map[id] || map['default'];
  };

  return (
    <>
      <TableRow index={index}>
        <TableCell>
          <IconButton
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              setOverlayDisplayed(!isOverlayDisplayed);
            }}
            color="primary"
          >
            <Icon
              name={
                isOverlayDisplayed
                  ? 'feather-chevron-down'
                  : 'feather-chevron-right'
              }
              width="24px"
              height="24px"
            />
          </IconButton>
        </TableCell>
        <TableCell>
          <H6 style={{ color: warning ? 'var(--ui-yellow)' : '' }}>
            {timepoint.SeriesDate}
          </H6>
        </TableCell>
        <TableCell>
          <H6>{round(timepoint.SliceThickness, -1)}</H6>
        </TableCell>
        {items(criteria.guidelineId).map(([key, options]) => (
          <TableCell key={key}>
            {isBaseline ? (
              <H6>---</H6>
            ) : isResponseConfirmed ? (
              <H6>{timepoint[key]?.result}</H6>
            ) : (
              <Select
                value={timepoint[key]?.result || ''}
                onChange={evt => {
                  const { SeriesInstanceUID: uid } = timepoint;
                  onAssessmentsChange(uid, key, evt.target.value);
                }}
                color="primary"
                highlight={timepoint[key]?.isOverridden}
              >
                {options.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            )}
          </TableCell>
        ))}
        <TableCell>
          <IconButton
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              if (!isResponseConfirming) {
                setIsResponseConfirming(true);
              }
            }}
            size="small"
            color="primary"
          >
            <Icon
              name={
                isResponseConfirming
                  ? 'feather-user-check'
                  : isResponseConfirmed
                  ? 'feather-check-square'
                  : 'feather-square'
              }
              width="24px"
              height="24px"
            />
          </IconButton>
        </TableCell>
      </TableRow>
      {!isBaseline &&
        isOverlayDisplayed &&
        notes(criteria.guidelineId).map(key => (
          <TableRow key={key} index={index}>
            <TableCell colSpan={3} />
            <TableCell colSpan={6}>
              <H6>{timepoint[key]?.message}</H6>
            </TableCell>
            <TableCell />
          </TableRow>
        ))}
      {isResponseConfirming && (
        <>
          <TableRow index={index}>
            <TableCell colSpan={2} />
            <TableCell>
              <H6>Reason:</H6>
            </TableCell>
            <TableCell colSpan={6}>
              <Input value={reason} onChange={e => setReason(e.target.value)} />
            </TableCell>
            <TableCell />
          </TableRow>
          {isPasswordInputDisplayed && (
            <TableRow index={index}>
              <TableCell colSpan={2} />
              <TableCell>
                <H6>Password:</H6>
              </TableCell>
              <TableCell colSpan={6}>
                <Input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                />
              </TableCell>
              <TableCell />
            </TableRow>
          )}
          <TableRow index={index}>
            <TableCell colSpan={7} />
            <TableCell>
              <Button
                onClick={() => {
                  setIsResponseConfirming(false);
                  setReason('');
                  setPassword('');
                }}
                color="secondary"
                variant="contained"
                fullWidth
              >
                Cancel
              </Button>
            </TableCell>
            <TableCell>
              <Button
                onClick={() => {
                  setIsResponseConfirmed(!isResponseConfirmed);
                  setIsResponseConfirming(false);
                  setReason('');
                  setPassword('');
                }}
                color="secondary"
                variant="contained"
                fullWidth
              >
                {isResponseConfirmed ? 'Unapprove' : 'Approve'}
              </Button>
            </TableCell>
            <TableCell />
          </TableRow>
        </>
      )}
    </>
  );
};
Row.propTypes = {
  index: PropTypes.number,
  timepoint: PropTypes.object,
  onAssessmentsChange: PropTypes.func,
  existingAlert: PropTypes.bool,
  isAlertDisplayed: PropTypes.bool,
  isPasswordInputDisplayed: PropTypes.bool,
};

/**
 * Inline best-overall-response picker for RECIP. Picks the "most favourable"
 * non-empty response across all assessed timepoints using the standard
 * oncology ordering CR > PR > SD > PD (NE/NED ignored). Skip the legacy
 * getBestOverallResponse helper which assumes RECIST-specific endpoint
 * inputs (per-lesion diameter responses) that RECIP doesn't produce.
 */
function getRecipBestOverall(assessments) {
  const order = ['CR', 'PR', 'SD', 'PD'];
  let bestIdx = -1;
  let bestResult = null;
  for (const a of assessments) {
    const r = a?.overallResponse?.result;
    if (!r) continue;
    const idx = order.indexOf(r);
    if (idx === -1) continue;
    if (bestIdx === -1 || idx < bestIdx) {
      bestIdx = idx;
      bestResult = r;
    }
  }
  return { result: bestResult || 'N/A' };
}
